import { format, isValid, parseISO } from 'date-fns'
import { upperFirst } from 'lodash-es'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  EMPTY_STRING,
} from '../../../constants/common'
import { CURRENCY_SYMBOL_MAP } from '../../../constants/currency'
import { DATE_SHORT_FORMAT } from '../../../constants/date'
import { formatNumberWithSeparators } from '../../../utils/number'

import {
  MATCH_CRITERION_LABEL_KEY_PREFIX,
  MATCH_CRITERION_STATUS,
  MATCH_CRITERION_STATUSES,
  MATCH_ELIGIBILITY,
  MATCH_ELIGIBILITY_STATUSES,
  MATCH_ENUM_LABEL_KEY,
  MATCH_ISO_DATE_PATTERN,
  MATCH_REASON_LABEL_KEY_PREFIX,
  MATCH_SCORE_REASON_INSUFFICIENT_COVERAGE,
  MATCH_VALUE_TYPE,
} from './RentalPortalMatch.constants'

function getMatchKeyLabel({ key, prefix }) {
  return `${prefix}${key.split('_').map(upperFirst).join(EMPTY_STRING)}Key`
}

function getMatchReasonCriterion({ criteria, reason }) {
  return criteria.find(criterion => criterion.reason === reason) ?? null
}

export function getMatchCriterionLabelKey(key) {
  return getMatchKeyLabel({ key, prefix: MATCH_CRITERION_LABEL_KEY_PREFIX })
}

export function getMatchReasonLabelKey(reason) {
  return getMatchKeyLabel({
    key: reason,
    prefix: MATCH_REASON_LABEL_KEY_PREFIX,
  })
}

export function getMatchCriterionStatusText({ criterion, t }) {
  return t(MATCH_CRITERION_STATUSES[criterion.status].text)
}

export function getMatchCriteria(match) {
  return match?.criteria ?? EMPTY_LIST
}

export function getHasMatchScore(match) {
  return Object.hasOwn(match ?? EMPTY_OBJECT, 'score')
}

export function getMatchScorePercent(score) {
  return Math.round(score * 100)
}

export function getMatchScoredCount(match) {
  return Object.keys(match?.weights_used?.raw ?? EMPTY_OBJECT).length
}

export function getMatchBreakdownCount(match) {
  return (match?.breakdown ?? EMPTY_LIST).length
}

export function getIsMatchEligible(eligibility) {
  return eligibility === MATCH_ELIGIBILITY.eligible
}

export function getIsMatchNotEligible(eligibility) {
  return eligibility === MATCH_ELIGIBILITY.notEligible
}

export function getHasMatchInsufficientCoverage(match) {
  return match.score_reason === MATCH_SCORE_REASON_INSUFFICIENT_COVERAGE
}

export function getMatchEligibilityText({ hasReason = true, match, t }) {
  const { eligibility, eligibility_reasons: reasons } = match

  const label = t(MATCH_ELIGIBILITY_STATUSES[eligibility].text)

  const [reason] = reasons ?? EMPTY_LIST

  if (!hasReason || !reason || getIsMatchEligible(eligibility)) {
    return label
  }

  const criterion = getIsMatchNotEligible(eligibility)
    ? getMatchReasonCriterion({ criteria: getMatchCriteria(match), reason })
    : null

  return `${label} — ${t(
    criterion
      ? getMatchCriterionLabelKey(criterion.key)
      : getMatchReasonLabelKey(reason)
  )}`
}

export function getIsMatchValueTypeEnum(criterion) {
  return criterion?.value_type === MATCH_VALUE_TYPE.enum
}

export function getIsMatchValueTypeMoney(criterion) {
  return criterion?.value_type === MATCH_VALUE_TYPE.money
}

export function getIsMatchValueTypeDurationMonths(criterion) {
  return criterion?.value_type === MATCH_VALUE_TYPE.durationMonths
}

export function getMatchCriterionValueText({
  criterion,
  currency,
  isRequirement,
  t,
  value,
}) {
  if (value == null) {
    return null
  }

  if (Array.isArray(value)) {
    return value
      .map(item =>
        getMatchCriterionValueText({
          criterion,
          currency,
          isRequirement,
          t,
          value: item,
        })
      )
      .join(', ')
  }

  if (getIsMatchValueTypeEnum(criterion)) {
    return t(MATCH_ENUM_LABEL_KEY[criterion.key]?.[value] ?? String(value))
  }

  if (getIsMatchValueTypeMoney(criterion)) {
    return `${formatNumberWithSeparators(value)} ${CURRENCY_SYMBOL_MAP[currency] ?? currency ?? EMPTY_STRING}`.trim()
  }

  if (getIsMatchValueTypeDurationMonths(criterion)) {
    return t(
      isRequirement
        ? 'RentalPortal.MatchDurationMinMonthsKey'
        : 'RentalPortal.MatchDurationMonthsKey',
      { count: Number(value) }
    )
  }

  const date = MATCH_ISO_DATE_PATTERN.test(value) ? parseISO(value) : null

  return date && isValid(date) ? format(date, DATE_SHORT_FORMAT) : String(value)
}

export function getIsMatchCriterionNotApplicable(criterion) {
  return criterion.status === MATCH_CRITERION_STATUS.notApplicable
}

export function getMatchExportValue({ match, t }) {
  const label = getMatchEligibilityText({ hasReason: false, match, t })

  return getHasMatchScore(match)
    ? `${label} · ${getMatchScorePercent(match.score)} % (${match.provided_count}/${match.criteria_count})`
    : label
}

export function getMatchCoverageText(match) {
  return `${match.provided_count}/${match.criteria_count}`
}
